import type { CoverageSchemaResponse } from '@/features/quotes/types/group-life/coverage-schema.types';
import { formatCurrencyBRL } from '@/features/quotes/utils/group-life/format';
import type { GroupLifeFrontCoverageSelection } from '@/services/group-life';

/**
 * Código de benefício de diárias → quantidade: `999300..999306` (DIH) e
 * `999400..999406` (incapacidade temporária) = 30/60/90/120/150/180/365. Espelho
 * de `DAILY_ALLOWANCE_STEPS` do mapper de emissão i4Pro (Portal.Api) — só entra
 * quando o nome da opção no schema não traz a quantidade.
 */
const DAILY_ALLOWANCE_STEPS = [30, 60, 90, 120, 150, 180, 365];
const DAILY_ALLOWANCE_RANGES = [999300, 999400];

/** "30 Diárias de Incapacidade Temporária" → 30. */
const QUANTITY_IN_NAME = /^\s*(\d+)\s*di[aá]rias?\b/i;

/** Quantidade de diárias da opção escolhida; `null` quando não é diária. */
export function dailyAllowanceCount(
  childCode: string | null | undefined,
  childName?: string
): number | null {
  const fromName = childName?.match(QUANTITY_IN_NAME);
  if (fromName) return Number(fromName[1]);

  const code = Number(childCode);
  if (!Number.isInteger(code)) return null;
  for (const base of DAILY_ALLOWANCE_RANGES) {
    const step = DAILY_ALLOWANCE_STEPS[code - base];
    if (step != null) return step;
  }
  return null;
}

/**
 * Detalhe das coberturas de diárias (DH, DIT) — "30 diárias × R$ 100,00" —,
 * indexado por CÓDIGO e por NOME da cobertura (o demonstrativo traz ora um,
 * ora outro). A quantidade é a opção escolhida no plano e o valor é o
 * `capital` gravado nela (o `SumInsured` da diária, não a quantidade).
 */
export function dailyAllowanceDetails(
  schema: CoverageSchemaResponse | undefined,
  selections: Record<string, GroupLifeFrontCoverageSelection> | undefined
): Map<string, string> {
  const details = new Map<string, string>();
  for (const coverage of schema?.coverages ?? []) {
    if (coverage.sumInsuredDefinition !== 'BENEFIT_SELECTION_WITH_SI') continue;
    const selection = selections?.[coverage.code];
    if (selection?.kind !== 'BENEFIT_SELECTION_WITH_SI' || !selection.enabled) {
      continue;
    }
    const child = coverage.children.find(
      item => item.code === selection.selectedChildCoverageCode
    );
    const count = dailyAllowanceCount(
      selection.selectedChildCoverageCode,
      child?.name
    );
    if (count == null) continue;
    const value =
      selection.capital != null && selection.capital > 0
        ? ` × ${formatCurrencyBRL(selection.capital)}`
        : '';
    const detail = `${count} diárias${value}`;
    details.set(coverage.code, detail);
    details.set(coverage.name, detail);
  }
  return details;
}
